/**
 * Modern Bold · <mb-product-gallery> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * PDP image gallery: prev/next arrows, thumbnail strip, mobile scroll-snap
 * with dot pager, and a fullscreen lightbox dialog. Self-contained — no
 * dependencies on other scripts.
 *
 * Public API:
 *   showMedia(mediaId)   — jump to the slide holding that media id. Also
 *                          driven automatically by `mb:variant-change`
 *                          (document-level, from modern-product-picker.js).
 *
 * Lifecycle:
 *   connectedCallback    — caches DOM refs, wires slider + lightbox
 *   disconnectedCallback — removes every listener and restores the
 *                          lightbox to its pre-portal DOM position so
 *                          a re-mount (shopify:section:load) doesn't
 *                          double-portal it.
 *
 * DOM contract (rendered by snippets/modern-product-gallery.liquid):
 *   <mb-product-gallery>
 *     [data-mb-gallery-stage]            slider stage
 *       [data-index][data-media-id] × N  individual slides
 *       [data-mb-gallery-zoom]           "click to zoom" trigger (optional)
 *       [data-mb-gallery-prev|next]      arrows (optional, ≥2 images)
 *     [data-mb-gallery-thumb] × N        thumbnails (optional)
 *     [data-mb-gallery-dot] × N          mobile dots (optional)
 *     [data-mb-gallery-lightbox]         dialog (optional)
 *       [data-mb-lightbox-stage]
 *         [data-lightbox-index] × N
 *         [data-mb-lightbox-prev|next]
 *       [data-mb-gallery-lightbox-close]
 *       [data-mb-gallery-lightbox-overlay]
 *       [data-mb-lightbox-current]       counter "n"
 */
if (!customElements.get('mb-product-gallery')) {
  // One intro scroll per page load — a section re-mount in the theme editor
  // must not replay it.
  let introScrollDone = false;

  class MBProductGallery extends HTMLElement {
    #cleanups = [];
    #stage = null;
    #slides = [];
    #thumbBtns = [];
    #thumbsEl = null;
    #dots = [];
    #counterEl = null;
    #prevBtn = null;
    #nextBtn = null;
    #lightbox = null;
    #current = 0;
    #galleryTop = '';
    #io = null;
    #inView = false;
    #lens = null;
    #zoomPanel = null;
    #zoomImg = null;
    #lastPointer = null;
    #zoomReady = new Set();

    /** Panel width bounds. Below the minimum there is no room worth zooming
     *  into, and the lightbox already covers that case. */
    static ZOOM_MIN_W = 380;
    static ZOOM_MAX_W = 620;
    static ZOOM_GAP = 14;
    static ZOOM_EDGE = 16;

    connectedCallback() {
      this.#stage     = this.querySelector('[data-mb-gallery-stage]');
      this.#slides    = Array.from(this.querySelectorAll('[data-mb-gallery-stage] [data-index]'));
      this.#thumbBtns = Array.from(this.querySelectorAll('[data-mb-gallery-thumb]'));
      this.#thumbsEl  = this.#thumbBtns[0] ? this.#thumbBtns[0].parentElement : null;
      this.#dots      = Array.from(this.querySelectorAll('[data-mb-gallery-dot]'));
      this.#counterEl = this.querySelector('[data-mb-gallery-current]');
      this.#prevBtn   = this.querySelector('[data-mb-gallery-prev]');
      this.#nextBtn   = this.querySelector('[data-mb-gallery-next]');
      this.#lightbox  = this.querySelector('[data-mb-gallery-lightbox]');

      if (!this.#slides.length) return;

      this.#adoptServerSlide();
      this.#wireSlider();
      this.#wireVideos();
      this.#wireAutoplay();
      this.#wireRail();
      this.#wireVariantSync();
      if (this.hasAttribute('data-mb-hover-zoom')) this.#wireHoverZoom();
      if (this.#lightbox) this.#wireLightbox();
      // Reflect the initial active slide (which may be a video) so the
      // zoom affordance hides from first paint without waiting for a nav.
      this.#updateMediaState(this.#current);
      this.#wireIntroScroll();
    }

    /**
     * Jump to the slide holding this media id. Public because the variant
     * picker lives in a SEPARATE Shopify section and can only reach the
     * gallery through the DOM — see #wireVariantSync.
     * No-op for a media id this product doesn't render (or the current one).
     */
    showMedia(mediaId) {
      if (!mediaId) return;
      const idx = this.#slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx >= 0 && idx !== this.#current) this.#goTo(idx);
    }

    /**
     * Follow the size/colour the shopper picks. `mb:variant-change` is
     * dispatched on document by modern-product-picker.js; featured_media
     * comes from `product.variants | json` (media id, NOT featured_image.id —
     * those are different numbers and only the media id matches a slide).
     *
     * Deliberately no focus move and no announcement: the picker already owns
     * the SR status for a variant change, and stealing focus off the size
     * radio the shopper just used would strand the keyboard.
     *
     * A variant without its own media falls back to the first slide, or the
     * previous variant's photo would stay up under the new selection. A null
     * variant (unavailable combination) leaves the stage where it is.
     */
    #wireVariantSync() {
      const onVariantChange = (e) => {
        const variant = e.detail?.variant;
        if (!variant) return;
        const id = String(variant.featured_media?.id ?? '');
        const idx = Math.max(0, this.#slides.findIndex((s) => s.dataset.mediaId === id));
        if (idx !== this.#current) this.#goTo(idx);
      };
      document.addEventListener('mb:variant-change', onVariantChange);
      this.#cleanups.push(() => document.removeEventListener('mb:variant-change', onVariantChange));
    }

    /**
     * The stage may open on a slide other than the first — Liquid marks the
     * selected variant's media is-active. Two things then have to catch up:
     * `current`, or the first arrow press would jump back to slide 1; and on
     * mobile the scroll-snap position, which the browser always starts at 0.
     *
     * Parked without animation (assigning scrollLeft, not scrollTo) so the
     * gallery never appears to swipe itself on load. The rAF re-run covers a
     * stage whose width isn't laid out yet at upgrade time.
     */
    #adoptServerSlide() {
      const idx = this.#slides.findIndex((s) => s.classList.contains('is-active'));
      if (idx <= 0) return;
      this.#current = idx;
      const park = () => {
        if (!this.#stage || !this.#isMobile()) return;
        const w = this.#stage.offsetWidth;
        if (w) this.#stage.scrollLeft = w * this.#current;
      };
      park();
      requestAnimationFrame(park);
    }

    /**
     * After load, if the page is still at the very top, ease the gallery up
     * under the sticky header — the stage is sized to the viewport but starts
     * ~285px down (breadcrumb + header), so its bottom would open below the
     * fold. Any other scroll position is the customer's, and is left alone.
     *
     * Waits for load deliberately: connectedCallback runs at ~90ms, before
     * DOMContentLoaded, where the tween raced the browser's own reset-to-top
     * and the scroll snapped back.
     */
    #wireIntroScroll() {
      if (introScrollDone) return;
      if (!window.matchMedia('(min-width: 990px)').matches) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (document.readyState === 'complete') { this.#runIntroScroll(); return; }
      const onLoad = () => this.#runIntroScroll();
      window.addEventListener('load', onLoad, { once: true });
      this.#cleanups.push(() => window.removeEventListener('load', onLoad));
    }

    #runIntroScroll() {
      if (introScrollDone || window.scrollY !== 0) return;

      const headerH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--mb-header-height'), 10
      ) || 0;
      const target = Math.round(this.getBoundingClientRect().top - headerH - 16);
      if (target < 24) return;

      introScrollDone = true;

      let raf = 0;
      const events = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
      const stop = () => {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        events.forEach((t) => window.removeEventListener(t, stop));
      };
      events.forEach((t) => window.addEventListener(t, stop, { passive: true }));
      this.#cleanups.push(stop);

      let t0 = 0;
      const step = (ts) => {
        if (!raf) return;
        if (!t0) t0 = ts;
        const p = Math.min(1, (ts - t0) / 480);
        window.scrollTo(0, target * (1 - (1 - p) ** 3));
        if (p < 1) raf = requestAnimationFrame(step);
        else stop();
      };
      raf = requestAnimationFrame(step);
    }

    /**
     * Publish how far down the page the gallery actually starts, so the CSS can
     * size the stage to the space that is genuinely left on screen.
     *
     * `100vh - header` was wrong by exactly the breadcrumb block: the gallery
     * begins 249px down on a 1512x950 desktop while the header var reads 137,
     * so the stage overran the fold by 112px and its bottom edge — counter,
     * zoom hint and all — sat below it. Measured from the document top, so the
     * value holds no matter where the page is scrolled when this runs.
     */
    #publishGalleryTop() {
      const top = `${Math.round(this.getBoundingClientRect().top + window.scrollY)}px`;
      // Write only on change: in --fit mode the rail's max-height reads this
      // var, so an unconditional write inside the ResizeObserver would feed
      // straight back into the observer.
      if (this.#galleryTop === top) return;
      this.#galleryTop = top;
      this.style.setProperty('--mb-pdp-gallery-top', top);
    }

    /**
     * Keep the rail's fade in step with its scroll position. Resize matters as
     * much as scroll: the rail's height is viewport-derived, so the same strip
     * can overflow at one window size and fit at another.
     *
     * The thumbnails are lazy-loaded, so scrollHeight is still settling on
     * first paint — ResizeObserver catches the reflow that a one-shot call at
     * connect would miss.
     */
    #wireRail() {
      const el = this.#thumbsEl;

      const paint = () => {
        this.#publishGalleryTop();
        this.#paintRailScroll();
      };

      window.addEventListener('resize', paint, { passive: true });
      this.#cleanups.push(() => window.removeEventListener('resize', paint));

      if (el) {
        // Scrolling the rail can't move the gallery's document-top — only the
        // fade needs repainting here.
        const onScroll = () => this.#paintRailScroll();
        el.addEventListener('scroll', onScroll, { passive: true });
        this.#cleanups.push(() => el.removeEventListener('scroll', onScroll));

        if (typeof ResizeObserver === 'function') {
          const ro = new ResizeObserver(paint);
          ro.observe(el);
          this.#cleanups.push(() => ro.disconnect());
        }
      }

      paint();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    // ── Slider ─────────────────────────────────────────────────────────────

    #isMobile() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    // Toggle the active state across slides, thumbnails and dots, and pull
    // the active thumbnail into view. Shared by #goTo (tap/arrow/keyboard)
    // and the mobile swipe-sync handler so both keep the thumb strip in
    // step — previously only the class flipped, so on a multi-image strip
    // the highlight could land on a thumb scrolled off-screen.
    #paintActive(n) {
      this.#slides.forEach((s, i) => {
        const active = i === n;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
      });
      this.#thumbBtns.forEach((btn, i) => {
        btn.classList.toggle('is-active', i === n);
        btn.setAttribute('aria-pressed', String(i === n));
      });
      this.#dots.forEach((dot, i) => dot.classList.toggle('is-active', i === n));
      if (this.#counterEl) this.#counterEl.textContent = String(n + 1);
      // A slide change under a parked cursor would keep magnifying the image
      // that just left; the next pointermove re-arms it.
      this.removeAttribute('data-mb-zoom-on');
      this.#scrollThumbIntoView(n);
      this.#updateMediaState(n);
    }

    // Pause any video that isn't on the active slide, and flag the gallery
    // when the active slide is a non-image (video / 3D) so the CSS can hide
    // the click-to-zoom affordance — the lightbox is an image-only zoom UI.
    // Also drives the in-view autoplay: the active slide's hosted <video>
    // plays (muted) while the gallery is on screen and pauses otherwise.
    #updateMediaState(n) {
      this.#slides.forEach((s, i) => {
        if (i === n) return;
        this.#programmaticPause(s.querySelector('video'));
      });
      const active = this.#slides[n];
      const type = active ? active.dataset.mediaType : 'image';
      this.classList.toggle('is-media-active', !!type && type !== 'image');

      // Autoplay the active hosted video while the gallery is in view; pause
      // it the moment it scrolls away or another slide becomes active. Muted
      // is mandatory — browsers reject a programmatic play() with audio
      // unless it follows a user gesture. External (YouTube/Vimeo) embeds are
      // facades/iframes, not <video>, so they're untouched here.
      //
      // The retained native controls win over the autoplay: once the user
      // pauses (__mbUserPaused) or unmutes (__mbUserUnmuted), a later in-view
      // re-entry or slide nav-back won't fight them. Those flags are
      // maintained by the per-video listeners wired in #wireAutoplay.
      const video = active && active.querySelector('[data-mb-gallery-video]');
      if (!video) return;
      if (this.#inView) {
        if (!video.__mbUserUnmuted) video.muted = true;
        if (!video.__mbUserPaused) {
          const p = video.play();
          if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay blocked — leave paused */ });
        }
      } else {
        this.#programmaticPause(video);
      }
    }

    // Pause a video we control without it being mistaken for a manual pause.
    // The `pause` event fires asynchronously, so __mbProg is read+cleared by
    // the listener (see #wireAutoplay). Guarded on `!paused` so a real pause
    // event always follows — otherwise the flag would leak to the next event.
    #programmaticPause(v) {
      if (!v || v.paused) return;
      v.__mbProg = true;
      try { v.pause(); } catch { v.__mbProg = false; }
    }

    // ── In-view autoplay ─────────────────────────────────────────────────────
    // Observe the stage and mirror its visibility into #inView, then re-run
    // #updateMediaState so the active video starts/stops with the scroll. No
    // observer is created when the gallery has no hosted video, when the API
    // is unavailable, or when the user prefers reduced motion (never autoplay).
    #wireAutoplay() {
      const videos = this.#slides
        .map((s) => s.querySelector('[data-mb-gallery-video]'))
        .filter(Boolean);
      if (!videos.length || typeof IntersectionObserver === 'undefined') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Capture manual intent off the native controls so the in-view autoplay
      // never overrides it. A `pause` event with no __mbProg flag is the user
      // pressing pause; once unmuted the user owns the audio state. onPlay
      // clears the paused flag so pressing play re-enables autoplay sync.
      videos.forEach((v) => {
        const onPause = () => {
          if (v.__mbProg) { v.__mbProg = false; return; }
          v.__mbUserPaused = true;
        };
        const onPlay = () => { v.__mbUserPaused = false; };
        const onVolume = () => { if (!v.muted) v.__mbUserUnmuted = true; };
        v.addEventListener('pause', onPause);
        v.addEventListener('play', onPlay);
        v.addEventListener('volumechange', onVolume);
        this.#cleanups.push(() => {
          v.removeEventListener('pause', onPause);
          v.removeEventListener('play', onPlay);
          v.removeEventListener('volumechange', onVolume);
        });
      });

      const target = this.#stage || this;
      this.#io = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        const next = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        if (next === this.#inView) return;
        this.#inView = next;
        this.#updateMediaState(this.#current);
      }, { threshold: [0, 0.5, 1] });
      this.#io.observe(target);

      this.#cleanups.push(() => {
        if (this.#io) { this.#io.disconnect(); this.#io = null; }
      });
    }

    // External-video facades: swap the poster button for the embed iframe
    // (cloned from the adjacent <template>) on first click, so YouTube/Vimeo
    // isn't requested on page view. Bound before the lightbox portals to
    // <body> so facades living inside it keep their listeners after the move.
    #wireVideos() {
      const facades = Array.from(this.querySelectorAll('[data-mb-gallery-video-facade]'));
      const handlers = facades.map((btn) => {
        const h = () => {
          const tmpl = btn.parentElement && btn.parentElement.querySelector('[data-mb-gallery-video-embed]');
          if (!tmpl) return;
          btn.replaceWith(tmpl.content.cloneNode(true));
        };
        btn.addEventListener('click', h);
        return [btn, h];
      });
      this.#cleanups.push(() => {
        handlers.forEach(([btn, h]) => btn.removeEventListener('click', h));
      });
    }

    // Scroll the active thumbnail into view within its own scroll container
    // only (scrollBy on the strip, never the page). Mobile strip scrolls
    // horizontally; the desktop rail scrolls vertically — pick the axis off
    // the same breakpoint the layout uses. Using rects (not offsetLeft)
    // keeps this correct regardless of the thumb's offset parent.
    #scrollThumbIntoView(idx) {
      const el = this.#thumbsEl;
      const btn = this.#thumbBtns[idx];
      if (!el || !btn) return;
      const er = el.getBoundingClientRect();
      const br = btn.getBoundingClientRect();
      if (this.#isMobile()) {
        el.scrollBy({ left: (br.left - er.left) - (er.width - br.width) / 2, behavior: 'smooth' });
      } else {
        el.scrollBy({ top: (br.top - er.top) - (er.height - br.height) / 2, behavior: 'smooth' });
      }
    }

    /**
     * Tell the CSS which end of the rail still has thumbnails behind it, so it
     * can fade that edge (see the [data-mb-rail] rules in modern-product.css).
     * Removing the attribute when nothing overflows matters: a fade on a rail
     * that is already showing everything would imply more images than exist.
     */
    #paintRailScroll() {
      const el = this.#thumbsEl;
      if (!el) return;
      const overflow = el.scrollHeight - el.clientHeight;
      if (this.#isMobile() || overflow <= 4) {
        el.removeAttribute('data-mb-rail');
        return;
      }
      const atStart = el.scrollTop <= 4;
      const atEnd = el.scrollTop >= overflow - 4;
      el.setAttribute('data-mb-rail', atStart ? 'start' : atEnd ? 'end' : 'middle');
    }


    /**
     * Amazon-style hover zoom. Three measured relationships drive all of it:
     *   factor = hiRes.naturalWidth / renderedWidth
     *   lens   = panel size / factor
     *   img    = -(lens offset x factor)
     *
     * The rendered image is contain-fitted, so it is usually letterboxed inside
     * the stage — the lens has to track the IMAGE box, not the stage, or the
     * zoom drifts by the size of the bands.
     */
    #wireHoverZoom() {
      this.#lens = this.querySelector('[data-mb-zoom-lens]');
      this.#zoomPanel = this.querySelector('[data-mb-zoom-panel]');
      this.#zoomImg = this.querySelector('[data-mb-zoom-img]');
      if (!this.#lens || !this.#zoomPanel || !this.#zoomImg || !this.#stage) return;

      const fine = window.matchMedia('(min-width: 990px) and (pointer: fine)');

      const onEnter = () => {
        if (!fine.matches || this.#activeIsMedia()) return;
        this.#loadZoomSource();
      };
      const onMove = (e) => {
        if (!fine.matches || this.#activeIsMedia()) return;
        this.#lastPointer = { clientX: e.clientX, clientY: e.clientY };
        this.#paintZoom(this.#lastPointer);
      };
      const onLeave = () => {
        this.#lastPointer = null;
        this.removeAttribute('data-mb-zoom-on');
      };

      // The first pointermove usually beats the hi-res download, and a cursor
      // held still fires no second one — so paint again when it arrives.
      const onZoomLoad = () => {
        if (this.#lastPointer) this.#paintZoom(this.#lastPointer);
      };
      this.#zoomImg.addEventListener('load', onZoomLoad);
      this.#cleanups.push(() => this.#zoomImg.removeEventListener('load', onZoomLoad));

      this.#stage.addEventListener('pointerenter', onEnter);
      this.#stage.addEventListener('pointermove', onMove);
      this.#stage.addEventListener('pointerleave', onLeave);
      this.#cleanups.push(() => {
        this.#stage.removeEventListener('pointerenter', onEnter);
        this.#stage.removeEventListener('pointermove', onMove);
        this.#stage.removeEventListener('pointerleave', onLeave);
      });
    }

    #activeIsMedia() {
      const slide = this.#slides[this.#current];
      return !slide || slide.dataset.mediaType !== 'image';
    }

    /**
     * Fetch the hi-res for a slide, once. Driven by hover, never by slide
     * changes: at ~630KB each, warming every thumbnail a shopper clicks would
     * cost tens of MB on a 33-image product for detail they never asked to see.
     */
    #prefetchZoom(index) {
      if (!this.#zoomImg) return;
      const src = this.#slides[index]?.dataset.zoomSrc;
      if (!src || this.#zoomReady.has(src)) return;
      const pre = new Image();
      pre.onload = () => {
        this.#zoomReady.add(src);
        // Only upgrade if this slide is still the one on screen and the cursor
        // is still on it.
        if (this.#slides[this.#current]?.dataset.zoomSrc === src && this.#lastPointer) {
          this.#loadZoomSource();
          this.#paintZoom(this.#lastPointer);
        }
      };
      pre.src = src;
    }

    /**
     * Points the panel at the sharpest source available right now: the hi-res
     * once it has landed, otherwise the image already on screen. The panel's
     * box is sized to the hi-res either way (#zoomBox), so the upgrade only
     * changes sharpness — the lens and the framing never jump.
     */
    #loadZoomSource() {
      const slide = this.#slides[this.#current];
      const hi = slide?.dataset.zoomSrc;
      if (!hi) return;
      const shown = slide.querySelector('.mb-pdp-gallery__img');
      const src = this.#zoomReady.has(hi) ? hi : shown?.currentSrc || hi;
      if (this.#zoomImg.getAttribute('src') !== src) this.#zoomImg.src = src;
      this.#prefetchZoom(this.#current);
    }

    /**
     * The magnified box, in hi-res pixels. Taken from the displayed image's
     * aspect rather than the hi-res file, so it is known before that file
     * exists — which is what lets the interim source stand in seamlessly.
     */
    #zoomBox(img) {
      const w = Number(this.dataset.zoomWidth) || 1800;
      return { w, h: Math.round((w * img.naturalHeight) / img.naturalWidth) };
    }

    #paintZoom(e) {
      const img = this.#slides[this.#current]?.querySelector('.mb-pdp-gallery__img');
      const zoomImg = this.#zoomImg;
      if (!img || !img.naturalWidth || !zoomImg.naturalWidth) return;

      // The contain-fitted box inside the stage, not the stage itself.
      const sr = this.#stage.getBoundingClientRect();
      const scale = Math.min(sr.width / img.naturalWidth, sr.height / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      const left = sr.left + (sr.width - w) / 2;
      const top = sr.top + (sr.height - h) / 2;

      if (e.clientX < left || e.clientX > left + w || e.clientY < top || e.clientY > top + h) {
        this.removeAttribute('data-mb-zoom-on');
        return;
      }

      // Fit the panel to the room actually left of the viewport edge. A fixed
      // width overflowed on narrower desktops — 155px past the edge at 1122,
      // which also gave the whole page a horizontal scrollbar.
      const host = this.getBoundingClientRect();
      const room =
        window.innerWidth - host.right - MBProductGallery.ZOOM_GAP - MBProductGallery.ZOOM_EDGE;
      if (room < MBProductGallery.ZOOM_MIN_W) {
        this.removeAttribute('data-mb-zoom-on');
        return;
      }
      this.#zoomPanel.style.width = `${Math.min(room, MBProductGallery.ZOOM_MAX_W)}px`;

      // Before measuring the panel: it is display:none until this lands, and a
      // hidden panel measures 0, which would size the lens to nothing.
      this.setAttribute('data-mb-zoom-on', '');

      const panel = this.#zoomPanel.getBoundingClientRect();
      const box = this.#zoomBox(img);
      zoomImg.style.width = `${box.w}px`;
      zoomImg.style.height = `${box.h}px`;
      const factor = box.w / w;
      const lensW = Math.min(panel.width / factor, w);
      const lensH = Math.min(panel.height / factor, h);

      const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
      const lensX = clamp(e.clientX - left - lensW / 2, 0, w - lensW);
      const lensY = clamp(e.clientY - top - lensH / 2, 0, h - lensH);

      // Lens is positioned against the gallery, the box both it and the panel
      // are absolutely placed in.
      Object.assign(this.#lens.style, {
        width: `${lensW}px`,
        height: `${lensH}px`,
        left: `${left - host.left + lensX}px`,
        top: `${top - host.top + lensY}px`,
      });

      zoomImg.style.left = `${-lensX * factor}px`;
      zoomImg.style.top = `${-lensY * factor}px`;
    }

    #goTo(idx) {
      const n = ((idx % this.#slides.length) + this.#slides.length) % this.#slides.length;

      this.#paintActive(n);

      // Mobile only — desktop stage is a CSS grid (no scroll). Calling
      // scrollTo on it would fire a phantom scroll event whose listener
      // resets `current` back to 0 (the original "next reverts" bug).
      if (this.#stage && this.#stage.scrollTo && this.#isMobile()) {
        this.#stage.scrollTo({
          left: this.#stage.offsetWidth * n,
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      if (this.#prevBtn) this.#prevBtn.setAttribute('aria-label', `Previous image (${n === 0 ? this.#slides.length : n} of ${this.#slides.length})`);
      if (this.#nextBtn) this.#nextBtn.setAttribute('aria-label', `Next image (${((n + 1) % this.#slides.length) + 1} of ${this.#slides.length})`);

      this.#current = n;
    }

    #wireSlider() {
      const onPrev    = () => this.#goTo(this.#current - 1);
      const onNext    = () => this.#goTo(this.#current + 1);
      const onKeydown = (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.#goTo(this.#current - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.#goTo(this.#current + 1); }
        if (e.key === 'Home')       { e.preventDefault(); this.#goTo(0); }
        if (e.key === 'End')        { e.preventDefault(); this.#goTo(this.#slides.length - 1); }
      };

      if (this.#prevBtn) this.#prevBtn.addEventListener('click', onPrev);
      if (this.#nextBtn) this.#nextBtn.addEventListener('click', onNext);
      this.addEventListener('keydown', onKeydown);

      const thumbHandlers = this.#thumbBtns.map((btn) => {
        const h = () => this.#goTo(parseInt(btn.dataset.index, 10));
        btn.addEventListener('click', h);
        return [btn, h];
      });

      this.#cleanups.push(() => {
        if (this.#prevBtn) this.#prevBtn.removeEventListener('click', onPrev);
        if (this.#nextBtn) this.#nextBtn.removeEventListener('click', onNext);
        this.removeEventListener('keydown', onKeydown);
        thumbHandlers.forEach(([btn, h]) => btn.removeEventListener('click', h));
      });

      // Mobile scroll-snap → dot/thumb sync. Bound only on mobile so the
      // desktop grid (which doesn't scroll) doesn't trigger phantom resets.
      if (this.#stage && this.#isMobile()) {
        let scrollTimer = 0;
        const onScroll = () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const idx = Math.round(this.#stage.scrollLeft / this.#stage.offsetWidth);
            if (idx === this.#current) return;
            this.#paintActive(idx);
            this.#current = idx;
          }, 80);
        };
        this.#stage.addEventListener('scroll', onScroll, { passive: true });
        this.#cleanups.push(() => {
          clearTimeout(scrollTimer);
          this.#stage.removeEventListener('scroll', onScroll);
        });
      }
    }

    // ── Lightbox ───────────────────────────────────────────────────────────

    #wireLightbox() {
      const lb = this.#lightbox;

      // Portal to <body> so the dialog escapes any sticky / transformed
      // ancestor that would otherwise create a stacking context above it.
      // Track the original parent so we can restore on disconnect.
      const originalParent = lb.parentElement;
      const originalNextSibling = lb.nextSibling;
      if (originalParent !== document.body) {
        document.body.appendChild(lb);
      }

      const lbSlides   = Array.from(lb.querySelectorAll('[data-lightbox-index]'));
      const lbPrev     = lb.querySelector('[data-mb-lightbox-prev]');
      const lbNext     = lb.querySelector('[data-mb-lightbox-next]');
      const lbClose    = lb.querySelector('[data-mb-gallery-lightbox-close]');
      const lbOverlay  = lb.querySelector('[data-mb-gallery-lightbox-overlay]');
      const lbCounter  = lb.querySelector('[data-mb-lightbox-current]');
      const zoomBtn    = this.querySelector('[data-mb-gallery-zoom]');
      let lbCurrent = 0;
      let lastTrigger = null;

      // Defer the 2400px lightbox images so they don't preload on page
      // view. Even with loading="lazy" the browser will fetch them once
      // the dialog gets portaled to <body> below, because the lazy
      // heuristic treats elements in document.body as eligible for the
      // initial scan. We sidestep that by stripping src/srcset on init
      // and restoring them per-slide the first time the modal activates
      // that slide (see ensureLoadingState).
      lbSlides.forEach((s) => {
        const img = s.querySelector('.mb-pdp-lightbox__img');
        if (!img) return;
        if (img.getAttribute('src')) {
          img.dataset.src = img.getAttribute('src');
          img.removeAttribute('src');
        }
        if (img.getAttribute('srcset')) {
          img.dataset.srcset = img.getAttribute('srcset');
          img.removeAttribute('srcset');
        }
      });
      // Third-party fixed-position widgets we squashed on open so they
      // don't paint over the dialog. Restored on close. Behavioural
      // detection (position:fixed + z-index >= 1000) catches Freshdesk,
      // a11y menus, cookie banners, etc. without an allow-list.
      let hiddenFloatingWidgets = [];

      // Track which slide images we've already wired a load listener to,
      // so re-navigating doesn't stack duplicate listeners.
      const wiredImages = new WeakSet();

      // Reset zoom + pan state on a single slide. Delegates to the
      // per-slide reset that wireZoomPan attached (which also clears the
      // closure-local scale/pan variables) when available, falling back
      // to a CSS-only reset for the brief window between init order.
      const resetZoom = (slide) => {
        if (!slide) return;
        if (typeof slide.__mbResetZoom === 'function') {
          slide.__mbResetZoom();
          return;
        }
        slide.classList.remove('is-zoomed', 'is-panning');
        const img = slide.querySelector('.mb-pdp-lightbox__img');
        if (img) {
          img.style.removeProperty('--mb-zoom-scale');
          img.style.removeProperty('--mb-zoom-tx');
          img.style.removeProperty('--mb-zoom-ty');
        }
      };

      const ensureLoadingState = (slide) => {
        if (!slide) return;
        const img = slide.querySelector('.mb-pdp-lightbox__img');
        if (!img) return;
        // First activation: restore the real src from data-src so the
        // browser only kicks off the 2400px fetch now, not on page load.
        if (!img.getAttribute('src') && img.dataset.src) {
          // Force eager loading — the original markup has loading="lazy"
          // and the slide may still be in a `hidden` ancestor or scrolled
          // off-screen in the scroll-snap stage. Some browsers defer
          // lazy loads in those states even when src is set programmatically.
          // Eager guarantees the fetch starts now.
          img.loading = 'eager';
          if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
          img.setAttribute('src', img.dataset.src);
        }
        const isReady = img.complete && img.naturalWidth > 0;
        if (isReady) {
          slide.classList.remove('is-loading');
          return;
        }
        slide.classList.add('is-loading');
        if (wiredImages.has(img)) return;
        wiredImages.add(img);
        const clear = () => slide.classList.remove('is-loading');
        img.addEventListener('load', clear, { once: true });
        img.addEventListener('error', clear, { once: true });
      };

      const lbGoTo = (idx) => {
        const n = ((idx % lbSlides.length) + lbSlides.length) % lbSlides.length;
        lbSlides.forEach((s, i) => {
          const active = i === n;
          s.classList.toggle('is-active', active);
          s.setAttribute('aria-hidden', String(!active));
          // Reset zoom on any slide we're moving away from — leaving an
          // off-screen slide zoomed would scale it the next time it
          // becomes active without an explicit user action.
          if (!active) {
            resetZoom(s);
            // Stop a video the user navigated away from so its audio
            // doesn't keep playing behind another slide.
            const v = s.querySelector('video');
            if (v && !v.paused) { try { v.pause(); } catch { /* ignore */ } }
          }
        });
        ensureLoadingState(lbSlides[n]);
        if (lbCounter) lbCounter.textContent = String(n + 1);
        lbCurrent = n;
      };

      const open = (idx, trigger) => {
        lastTrigger = trigger || document.activeElement;
        lbGoTo(typeof idx === 'number' ? idx : this.#current);
        lb.hidden = false;
        // Force reflow so the entry transition runs
        // eslint-disable-next-line no-unused-expressions
        lb.offsetHeight;
        lb.classList.add('is-open');

        // iOS-safe scroll lock. `overflow: hidden` on body doesn't stop
        // touch scroll on mobile Safari — the page underneath continues
        // to scroll behind the modal. The position:fixed + top:-scrollY
        // pattern is the standard fix: take body out of the document
        // flow at its current scroll offset, restore on close.
        const scrollY = window.scrollY || window.pageYOffset || 0;
        document.body.dataset.mbLightboxScroll = String(scrollY);
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';

        hiddenFloatingWidgets = [];
        Array.from(document.body.children).forEach((el) => {
          if (el === lb || el.contains(lb)) return;
          if (!el.hasAttribute('data-mb-was-inert')) {
            el.setAttribute('data-mb-was-inert', el.hasAttribute('inert') ? '1' : '0');
            el.setAttribute('inert', '');
          }
          const cs = getComputedStyle(el);
          const z = parseInt(cs.zIndex, 10);
          if (cs.position === 'fixed' && !Number.isNaN(z) && z >= 1000 && cs.display !== 'none') {
            hiddenFloatingWidgets.push({ el, prevDisplay: el.style.display });
            el.style.setProperty('display', 'none', 'important');
          }
        });
        requestAnimationFrame(() => lbClose && lbClose.focus());
      };

      const close = () => {
        lb.classList.remove('is-open');
        // Always exit zoomed state when closing — opening fresh next time
        // should start at scale 1, not whatever the last view was at.
        lbSlides.forEach(resetZoom);
        // Pause any playing video so it doesn't keep running after close.
        lbSlides.forEach((s) => {
          const v = s.querySelector('video');
          if (v && !v.paused) { try { v.pause(); } catch { /* ignore */ } }
        });

        // Restore the iOS-safe scroll lock and scroll the user back to
        // where they were. Reading the saved offset off the body's data
        // attribute keeps this resilient if multiple opens stack up
        // (unlikely but safe).
        const savedScroll = parseInt(document.body.dataset.mbLightboxScroll || '0', 10);
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        delete document.body.dataset.mbLightboxScroll;
        window.scrollTo(0, savedScroll);
        Array.from(document.body.children).forEach((el) => {
          if (el.hasAttribute('data-mb-was-inert')) {
            if (el.getAttribute('data-mb-was-inert') === '0') el.removeAttribute('inert');
            el.removeAttribute('data-mb-was-inert');
          }
        });
        hiddenFloatingWidgets.forEach(({ el, prevDisplay }) => {
          if (prevDisplay) el.style.setProperty('display', prevDisplay);
          else el.style.removeProperty('display');
        });
        hiddenFloatingWidgets = [];
        setTimeout(() => { lb.hidden = true; }, 250);
        if (lbCurrent !== this.#current) this.#goTo(lbCurrent);
        if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
      };

      // ── Zoom + pan wiring ────────────────────────────────────────────────
      // One handler set per slide image. Behaviour:
      //   • pointerdown/move/up on the image → click toggles zoom; drag
      //     pans (when already zoomed)
      //   • wheel on the image while zoomed → pan via deltaX/deltaY
      //     (trackpad two-finger scroll natively generates wheel events)
      //   • Click-vs-drag is distinguished by movement threshold (5px). A
      //     pointerup with no significant movement is treated as click.
      //   • Pan offsets clamped to the slide bounds so the image always
      //     overlaps the centre of the viewport.
      //   • setPointerCapture keeps move/up events flowing to the image
      //     even if the cursor leaves it mid-drag — no document-level
      //     listener needed.
      const MIN_SCALE = 1;
      const MAX_SCALE = 6;
      const CLICK_ZOOM = 4;       // jump-zoom level on a plain click
      const WHEEL_SENSITIVITY = 0.005;
      const MOVE_THRESHOLD = 5;
      const zoomCleanups = [];

      const wireZoomPan = (slide, img) => {
        // Progressive zoom state. scale ∈ [MIN_SCALE, MAX_SCALE]; panX/panY
        // are visual-pixel translations applied after the scale.
        let scale = 1;
        let panX = 0, panY = 0;
        let baseX = 0, baseY = 0;
        let startX = 0, startY = 0;
        let dragging = false;
        let moved = false;

        // Multi-touch state for pinch-zoom on mobile.
        //   activePointers: pointerId → { x, y } of every finger currently down
        //   pinchStartDist / pinchStartScale: baseline captured when the
        //     second finger lands, used to derive the pinch ratio
        //   gestureHadMultitouch: latched true on any 2+ pointer event so
        //     the eventual single-finger pointerup doesn't get misread as
        //     a tap and re-trigger click-to-zoom on top of the pinch
        const activePointers = new Map();
        let pinchStartDist = 0;
        let pinchStartScale = 1;
        let gestureHadMultitouch = false;

        // The image's rendered (pre-transform) dimensions — image uses
        // max-width/max-height 100% + object-fit:contain inside the slide,
        // so a portrait image in a landscape slide is letterboxed and the
        // visible image is narrower than the slide. Pan bounds must be
        // computed from this real image rect, NOT the slide rect, or we
        // let the image edge fly past the viewport edge into the void.
        const renderedImageBounds = () => {
          const sr = slide.getBoundingClientRect();
          if (!img.naturalWidth || !img.naturalHeight) {
            return { w: sr.width, h: sr.height };
          }
          const imgRatio = img.naturalWidth / img.naturalHeight;
          const slideRatio = sr.width / sr.height;
          if (imgRatio > slideRatio) {
            return { w: sr.width, h: sr.width / imgRatio };
          }
          return { w: sr.height * imgRatio, h: sr.height };
        };

        const maxOffsets = (s = scale) => {
          const b = renderedImageBounds();
          const sr = slide.getBoundingClientRect();
          // Clamp pan so the scaled image edge can travel exactly to the
          // slide edge — never past. If the scaled image is narrower than
          // the slide in some axis (extreme aspect mismatch + low scale),
          // the result would be negative; Math.max(0, …) keeps it at 0.
          return {
            x: Math.max(0, (s * b.w - sr.width) / 2),
            y: Math.max(0, (s * b.h - sr.height) / 2),
          };
        };

        // Apply a scale change with a focal point (screen coords). Cursor
        // pixel under the focal point stays put: panNew = pan·r + dx·(1−r)
        // where r = newScale/oldScale and dx is cursor offset from image
        // centre. Toggles .is-zoomed when crossing the scale=1 boundary
        // and writes the scale + pan CSS vars in a single batch.
        const setScale = (newScale, focalX, focalY) => {
          const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
          if (s === scale) return;
          if (s === 1) {
            // Snap back to identity — no need for focal-point math at
            // the un-zoomed state.
            scale = 1;
            panX = 0; panY = 0;
            slide.classList.remove('is-zoomed', 'is-panning');
            img.style.removeProperty('--mb-zoom-scale');
            img.style.removeProperty('--mb-zoom-tx');
            img.style.removeProperty('--mb-zoom-ty');
            return;
          }
          if (focalX != null && focalY != null) {
            // Use slide rect (NOT img rect) as the reference origin.
            // The img element is what gets transformed by scale + pan,
            // so its getBoundingClientRect() returns the CURRENT visible
            // bbox — which shifts every frame during pinch and causes
            // the zoom to oscillate. The slide doesn't transform, so its
            // centre stays put, and (thanks to the slide's flex centring
            // of the image) it coincides with the image's natural
            // pre-transform centre — exactly what the focal-point pan
            // math requires.
            const r = slide.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            const dx = focalX - cx;
            const dy = focalY - cy;
            const ratio = s / scale;
            panX = panX * ratio + dx * (1 - ratio);
            panY = panY * ratio + dy * (1 - ratio);
          }
          scale = s;
          // Re-clamp the pan in case the new scale's bound is tighter
          // than the previous pan position (e.g. zooming out).
          const m = maxOffsets();
          panX = Math.max(-m.x, Math.min(m.x, panX));
          panY = Math.max(-m.y, Math.min(m.y, panY));
          slide.classList.add('is-zoomed');
          img.style.setProperty('--mb-zoom-scale', String(scale));
          img.style.setProperty('--mb-zoom-tx', `${panX}px`);
          img.style.setProperty('--mb-zoom-ty', `${panY}px`);
        };
        const clamp = (v, m) => Math.max(-m, Math.min(m, v));
        const applyPan = (x, y) => {
          panX = x; panY = y;
          img.style.setProperty('--mb-zoom-tx', `${x}px`);
          img.style.setProperty('--mb-zoom-ty', `${y}px`);
        };

        const onDown = (e) => {
          // Multi-touch (pinch) is owned by the TouchEvent handlers below
          // — PointerEvent multi-touch is unreliable on iOS Safari when
          // touch-action: none is set on an ancestor. Bail out of the
          // single-pointer drag path if another finger is already down.
          if (e.pointerType === 'touch' && gestureHadMultitouch) return;
          if (e.button !== undefined && e.button !== 0) return;
          startX = e.clientX; startY = e.clientY;
          baseX = panX; baseY = panY;
          dragging = true; moved = false;
          if (img.setPointerCapture) {
            try { img.setPointerCapture(e.pointerId); } catch { /* ignore */ }
          }
        };

        const onMove = (e) => {
          if (gestureHadMultitouch) return;
          if (!dragging) return;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (!moved && Math.hypot(dx, dy) > MOVE_THRESHOLD) {
            moved = true;
            if (scale > 1) slide.classList.add('is-panning');
          }
          if (moved && scale > 1) {
            const m = maxOffsets();
            applyPan(clamp(baseX + dx, m.x), clamp(baseY + dy, m.y));
          }
        };

        const onUp = (e) => {
          slide.classList.remove('is-panning');
          dragging = false;
          if (img.releasePointerCapture) {
            try { img.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
          }
          // Suppress the tap-toggle when the gesture involved a pinch —
          // the TouchEvent handlers set gestureHadMultitouch and the
          // trailing pointerup (lifting the last finger) shouldn't be
          // misread as a tap that re-applies CLICK_ZOOM on top.
          if (gestureHadMultitouch) {
            // Clear the flag only when no fingers remain — otherwise a
            // brief second-finger lift in the middle of a pinch would
            // un-suppress the next pointerup mid-gesture.
            if (!activePointers.size) gestureHadMultitouch = false;
            return;
          }
          // Un-zoomed horizontal swipe → previous / next slide.
          // Threshold: 50px AND horizontal travel > 1.5× vertical so
          // a near-vertical drag (e.g. user about to pinch) doesn't
          // accidentally flip the slide. Only fires at scale === 1
          // because a drag while zoomed is pan, not nav.
          if (moved && scale === 1) {
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
              if (dx < 0) lbGoTo(lbCurrent + 1);
              else        lbGoTo(lbCurrent - 1);
            }
            return;
          }

          if (!moved) {
            // Pointerup without significant movement → treat as click.
            // Toggle between un-zoomed and the click-zoom level,
            // regardless of any intermediate wheel-zoom state. Cursor
            // position is the focal point for the zoom-in transition.
            if (scale > 1) {
              setScale(1);
            } else {
              setScale(CLICK_ZOOM, e.clientX, e.clientY);
            }
          }
        };

        // ── TouchEvent-based pinch ──────────────────────────────────────
        // iOS Safari with touch-action:none reliably fires TouchEvents
        // with the full e.touches list for multi-touch, where its
        // PointerEvent stream can drop the second pointer. We use
        // TouchEvents exclusively for pinch and let PointerEvents
        // handle the single-finger drag + click path above.
        const onTouchStart = (e) => {
          // Maintain a tiny presence-count so the pointerup handler
          // knows when the last finger has lifted.
          for (let i = 0; i < e.changedTouches.length; i++) {
            activePointers.set(e.changedTouches[i].identifier, true);
          }
          if (e.touches.length >= 2) {
            const t0 = e.touches[0];
            const t1 = e.touches[1];
            pinchStartDist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
            pinchStartScale = scale;
            gestureHadMultitouch = true;
            // Cancel any drag in flight — the gesture has become a pinch.
            dragging = false;
            slide.classList.remove('is-panning');
            // CSS class disables the .25s transform transition so each
            // touchmove tracks the fingers 1:1 instead of animating
            // through intermediate scales (which read as oscillation).
            slide.classList.add('is-pinching');
          }
        };

        const onTouchMove = (e) => {
          if (e.touches.length >= 2 && pinchStartDist > 0) {
            // preventDefault so iOS doesn't try to do anything else with
            // the gesture (it shouldn't with touch-action:none, but the
            // explicit cancel is defence in depth).
            e.preventDefault();
            const t0 = e.touches[0];
            const t1 = e.touches[1];
            const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
            const newScale = pinchStartScale * (dist / pinchStartDist);
            const fx = (t0.clientX + t1.clientX) / 2;
            const fy = (t0.clientY + t1.clientY) / 2;
            setScale(newScale, fx, fy);
          }
        };

        const onTouchEnd = (e) => {
          for (let i = 0; i < e.changedTouches.length; i++) {
            activePointers.delete(e.changedTouches[i].identifier);
          }
          if (e.touches.length < 2) {
            // Pinch broken — clear the start distance so a subsequent
            // 2-finger gesture starts fresh.
            pinchStartDist = 0;
            slide.classList.remove('is-pinching');
          }
          // Clear the multitouch latch the moment the last finger is
          // off the screen. On iOS Safari pointerup can fire BEFORE the
          // matching touchend updates activePointers, so doing this in
          // the pointerup handler reads a stale "still pinching" state
          // and the latch never clears — which then blocks the next
          // single-finger drag (onDown returns early on touch pointers
          // when gestureHadMultitouch is still true).
          if (e.touches.length === 0) {
            gestureHadMultitouch = false;
          }
        };

        // Wheel / two-finger trackpad scroll → progressive zoom around
        // the cursor. Negative deltaY (scroll up / two-finger swipe up
        // on most trackpads) zooms in; positive zooms out. The step is
        // proportional to current scale (multiplicative) so zooming in
        // feels uniform across the range. preventDefault stops the
        // wheel from also scrolling the page underneath.
        const onWheel = (e) => {
          e.preventDefault();
          const step = -e.deltaY * WHEEL_SENSITIVITY;
          const newScale = scale * (1 + step);
          setScale(newScale, e.clientX, e.clientY);
        };

        // Per-slide reset exposed to the outer resetZoom() helper.
        // Clears the closure-local scale/pan state alongside the CSS,
        // so navigating away from a wheel-zoomed slide really does
        // start fresh next time it becomes active.
        const reset = () => {
          scale = 1;
          panX = 0; panY = 0;
          slide.classList.remove('is-zoomed', 'is-panning', 'is-pinching');
          img.style.removeProperty('--mb-zoom-scale');
          img.style.removeProperty('--mb-zoom-tx');
          img.style.removeProperty('--mb-zoom-ty');
        };
        slide.__mbResetZoom = reset;
        // Multiplicative scale step exposed for keyboard zoom (+ / −).
        // No focal point → setScale defaults to centred zoom, which is
        // what a keyboard user expects (no cursor / pinch midpoint).
        slide.__mbScaleBy = (factor) => setScale(scale * factor);

        img.addEventListener('pointerdown', onDown);
        img.addEventListener('pointermove', onMove);
        img.addEventListener('pointerup', onUp);
        img.addEventListener('pointercancel', onUp);
        img.addEventListener('wheel', onWheel, { passive: false });
        // TouchEvents bound on the SLIDE (parent of img) so a pinch
        // that starts on the dark letterbox area is still captured.
        // passive:false on touchmove so we can preventDefault during
        // pinch and stop iOS from doing anything else with the gesture.
        slide.addEventListener('touchstart', onTouchStart, { passive: true });
        slide.addEventListener('touchmove', onTouchMove, { passive: false });
        slide.addEventListener('touchend', onTouchEnd);
        slide.addEventListener('touchcancel', onTouchEnd);

        return () => {
          img.removeEventListener('pointerdown', onDown);
          img.removeEventListener('pointermove', onMove);
          img.removeEventListener('pointerup', onUp);
          img.removeEventListener('pointercancel', onUp);
          img.removeEventListener('wheel', onWheel);
          slide.removeEventListener('touchstart', onTouchStart);
          slide.removeEventListener('touchmove', onTouchMove);
          slide.removeEventListener('touchend', onTouchEnd);
          slide.removeEventListener('touchcancel', onTouchEnd);
          delete slide.__mbResetZoom;
          delete slide.__mbScaleBy;
        };
      };

      lbSlides.forEach((s) => {
        const img = s.querySelector('.mb-pdp-lightbox__img');
        if (img) zoomCleanups.push(wireZoomPan(s, img));
      });

      const handlers = [];
      const bind = (el, evt, fn) => { el.addEventListener(evt, fn); handlers.push([el, evt, fn]); };

      if (this.#stage) {
        bind(this.#stage, 'click', (e) => {
          // Bail on any interactive descendant — clicks on the wishlist
          // heart (mb-wishlist-button is a custom element, not a literal
          // <button>), arrow nav, zoom hint, the inline <video> player, or
          // any link should fire their own handler, not pop the lightbox.
          if (e.target.closest('button, a, mb-wishlist-button, [role="button"], video')) return;
          // Video / 3D slides aren't zoomable — let the inline player own the
          // interaction instead of opening the image-zoom lightbox.
          const active = this.#slides[this.#current];
          if (active && active.dataset.mediaType && active.dataset.mediaType !== 'image') return;
          open(this.#current, this.#stage);
        });
      }
      if (zoomBtn) {
        bind(zoomBtn, 'click', (e) => {
          e.stopPropagation();
          open(this.#current, zoomBtn);
        });
      }
      if (lbClose)   bind(lbClose,   'click', close);
      if (lbOverlay) bind(lbOverlay, 'click', close);
      if (lbPrev)    bind(lbPrev,    'click', () => lbGoTo(lbCurrent - 1));
      if (lbNext)    bind(lbNext,    'click', () => lbGoTo(lbCurrent + 1));

      bind(lb, 'keydown', (e) => {
        if (e.key === 'Escape')     { e.preventDefault(); close(); }
        if (e.key === 'ArrowLeft')  { e.preventDefault(); lbGoTo(lbCurrent - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); lbGoTo(lbCurrent + 1); }
        if (e.key === 'Home')       { e.preventDefault(); lbGoTo(0); }
        if (e.key === 'End')        { e.preventDefault(); lbGoTo(lbSlides.length - 1); }
        // Keyboard zoom — mirrors the browser's native zoom shortcuts
        // (+ in, − out, 0 reset). Routes through the active slide's
        // exposed __mbScaleBy / __mbResetZoom helpers so the centred
        // zoom uses the same setScale + clamp pipeline as wheel/pinch.
        // `=` is the unshifted key on a US layout where + lives, so
        // both keys map to zoom-in.
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          const slide = lbSlides[lbCurrent];
          if (slide && slide.__mbScaleBy) slide.__mbScaleBy(1.25);
        }
        if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          const slide = lbSlides[lbCurrent];
          if (slide && slide.__mbScaleBy) slide.__mbScaleBy(0.8);
        }
        if (e.key === '0') {
          e.preventDefault();
          const slide = lbSlides[lbCurrent];
          if (slide && slide.__mbResetZoom) slide.__mbResetZoom();
        }
      });


      this.#cleanups.push(() => {
        handlers.forEach(([el, evt, fn]) => el.removeEventListener(evt, fn));
        zoomCleanups.forEach((fn) => fn());
        // Restore the lightbox to its original position so a re-mount
        // (theme-editor section reload) doesn't double-portal it to body.
        if (originalParent && lb.parentElement === document.body) {
          if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
            originalParent.insertBefore(lb, originalNextSibling);
          } else {
            originalParent.appendChild(lb);
          }
        }
      });
    }
  }
  customElements.define('mb-product-gallery', MBProductGallery);
}
